"use client";

import { useEffect, useState } from "react";
import Modal from "~/components/v3/themed/Modal";
import {
    FaLink,
    FaXTwitter,
    FaInstagram,
    FaFacebook,
    FaLinkedin,
} from "react-icons/fa6";
import { useEventContext } from "~/app/providers";
import { Card } from "~/components/v4/shadcn/Card";
import { Avatar, AvatarImage } from "~/components/v4/shadcn/Avatar";
import { getEventImageUrl } from "~/utils/images";
import { getCommunityImageUrl } from "~/components/v4/ui-helpers/community";
import { DateTime } from "luxon";
import RiverStar from "~/components/v4/icons/RiverStar";
import { motion } from "framer-motion";
import { GetEventFromSlug } from "~/app/beta/events/[eventSlug]/actions";
import { Sheet, SheetContent } from "~/components/v3/shadcn/Sheet";
import { useIsMobileV2 } from "~/hooks/useIsMobile";
import { useQRCode } from "next-qrcode";
import { APP_URL } from "~/utils/constants";
import { IoCopyOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import { CommunityAvatar } from "~/components/v4/shadcn/CommunityAvatar";
import { env } from "~/env.mjs";
import { useShareCommunityFunctions } from "~/app/events/[slug]/share/_components/useShareFunctions";
const ModalOrSheet = ({
    open,
    onOpenChange,
    title,
    children,
    type,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    children: React.ReactNode;
    type: "modal" | "sheet";
}) => {
    useEffect(() => {
        if (
            type === "sheet" &&
            typeof window !== "undefined" &&
            window.Intercom
        ) {
            window.Intercom("update", { hide_default_launcher: open });
        }
    }, [open, type]);
    if (type === "sheet")
        return (
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent side="bottom" showCloseIcon={true}>
                    {children}
                </SheetContent>
            </Sheet>
        );
    return (
        <Modal title={title} open={open} onOpenChange={onOpenChange}>
            {children}
        </Modal>
    );
};

type ShareComponentProps = {
    isVisible: boolean;
    setIsVisible: (show: boolean) => void;
    community: {
        id: string;
        slug: string;
        name: string;
        image?: string | null;
    };
};

const ShareCommunityModal = ({
    isVisible,
    setIsVisible,
    community,
}: ShareComponentProps) => {
    const isMobile = useIsMobileV2();
    const [copiedLink, setCopiedLink] = useState(false);
    const { Image: QrCodeImage } = useQRCode();
    const { shareToX, shareToLinkedIn, shareToInstagram, shareToFacebook } =
        useShareCommunityFunctions(community.slug);

    // Hide intercom button when modal is open
    useEffect(() => {
        if (typeof window === "undefined" || !window.Intercom) return;
        if (isVisible) {
            window.Intercom("update", { hide_default_launcher: true });
        } else {
            window.Intercom("update", { hide_default_launcher: false });
        }
    }, [isVisible]);

    const copyToClipboardWithToast = (text: string, platform: string) => {
        if (!navigator) throw new Error("Navigator not found");
        navigator.clipboard.writeText(text);
        toast.success(`Copied ${platform} share link to clipboard!`, {
            autoClose: 500,
        });
    };

    const copyLink = async () => {
        copyToClipboardWithToast(`${APP_URL}/${community.slug}`, "Community");
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
    };

    const shareOptions = [
        {
            name: "Copy Link",
            icon: IoCopyOutline,
            iconColor: "#000",
            backgroundColor: "#E2FF05",
            action: copyLink,
        },
        {
            name: "Share to X",
            icon: FaXTwitter,
            backgroundColor: "#000",
            action: shareToX,
        },
        {
            name: "Share to LinkedIn",
            icon: FaLinkedin,
            backgroundColor: "#0A66C2",

            action: shareToLinkedIn,
        },
        {
            name: "Share to Instagram",
            icon: FaInstagram,
            background:
                "linear-gradient(145deg, #f09433 0%, #e6683c 25%, #e6683c 27%, #dc2743 40%, #dc2743 50%, #dc2743 55%, #bc1888 63%, #bc1888 100%)",
            action: shareToInstagram,
        },
        {
            name: "Share to Facebook",
            icon: FaFacebook,
            backgroundColor: "#1877F2",
            action: shareToFacebook,
        },
    ];

    return (
        <ModalOrSheet
            title="Share community"
            open={isVisible}
            onOpenChange={setIsVisible}
            type={isMobile ? "sheet" : "modal"}
        >
            {/* Enhanced Event Card */}
            <Card className="group relative flex flex-col items-center justify-center gap-2 overflow-hidden rounded-[20px] bg-v3-N550 pb-6 will-change-transform">
                {/* Animated border overlay */}
                <div className="absolute inset-0 rounded-[20px] border border-transparent transition-all will-change-[box-shadow] group-hover:shadow-[0_0_20px_rgba(14,170,21,0.15)]" />

                <div className="flex w-[256px] flex-col gap-4 pt-4">
                    {/* Community/Profile info */}
                    <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8 flex-shrink-0 border-2 border-v4-primary">
                            <AvatarImage
                                src={getCommunityImageUrl(
                                    community?.id,
                                    community?.image,
                                )}
                                alt="host image"
                                width={96}
                                height={96}
                            />
                        </Avatar>
                        <p className="text-lg font-bold">{community.name}</p>
                    </div>
                </div>
                {/* Card content */}
                <div className="p-4">
                    <div className="flex flex-col items-center">
                        <QrCodeImage
                            text={`${APP_URL}/${community.slug}`}
                            options={{
                                width: 256,
                                margin: 0,
                                color: {
                                    dark: "#000000",
                                    light: "#fff",
                                },
                            }}
                        />
                    </div>
                </div>
            </Card>

            {/* Share options */}
            <div className="mb-4 mt-4 grid grid-cols-5 gap-x-3 gap-y-6 px-3">
                {shareOptions.map((option) => (
                    <div
                        key={option.name}
                        className="flex flex-col items-center"
                    >
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => option.action()}
                            className="flex h-12 w-12 items-center justify-center rounded-full shadow-lg transition-all duration-200"
                            style={{
                                ...(option.backgroundColor && {
                                    backgroundColor: option.backgroundColor,
                                }),
                                ...(option.background && {
                                    background: option.background,
                                }),
                            }}
                        >
                            <option.icon size={22} color={option.iconColor} />
                        </motion.button>
                    </div>
                ))}
            </div>

            {/* Copied notification */}
            {copiedLink ? (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="mt-4 rounded-lg bg-v3-C400/10 p-2 text-center text-sm font-medium text-v3-C400"
                >
                    Link copied to clipboard!
                </motion.div>
            ) : (
                <span className="block h-10 w-full"></span>
            )}
        </ModalOrSheet>
    );
};

export default ShareCommunityModal;
